import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Upload, AlertCircle, CheckCircle, Trash2, Loader2, Info, Inbox,
  ChevronLeft, ChevronRight, Search, Eye, ExternalLink, X,
} from 'lucide-react';

const ITEMS_PER_PAGE = 25;

const FILENAME_REGEX = /^(.+?)_(.+?)_(.+?)\.pdf$/i;

const parseAlbaranFilename = (filename) => {
  const match = filename.match(FILENAME_REGEX);
  if (!match) return null;
  const [, cliente, referencia, nombreAlbaran] = match;
  return {
    cliente,
    referencia: referencia.replace(/&/g, '/'),
    nombreAlbaran,
  };
};

/* ---------- Dropzone ---------- */
const Dropzone = ({ onFiles, disabled }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (files.length) onFiles(files.slice(0, 1000));
  }, [onFiles, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback(() => setDragging(false), []);

  const handleClick = () => { if (!disabled) inputRef.current?.click(); };

  const handleChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 1000);
    if (files.length) onFiles(files);
    e.target.value = '';
  };

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
        dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <Upload size={32} className="mx-auto text-gray-400 mb-2" />
      <p className="text-gray-600 font-medium text-sm">Arrastre archivos PDF aquí o haga clic para seleccionar</p>
      <p className="text-xs text-gray-400 mt-1">Máximo 1000 archivos · Solo PDF</p>
    </div>
  );
};

/* ========== ALBARAN UPLOAD ========== */
const AlbaranUpload = () => {
  const { token, API_URL } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [parsedFiles, setParsedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');

  const FTP_ALBARAN_BASE = 'https://comprassanitarias.com/albaranes';
  const [previewAlbaran, setPreviewAlbaran] = useState(null);

  // Albaranes listing
  const [albaranes, setAlbaranes] = useState([]);
  const [albaranesLoading, setAlbaranesLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAlbaranes, setTotalAlbaranes] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAlbaranes = useCallback(async (p = 1, search = '') => {
    setAlbaranesLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: ITEMS_PER_PAGE });
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/commercial/albaranes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar albaranes');
      const data = await res.json();
      setAlbaranes(data.albaranes || data.data || []);
      const total = data.pagination?.total || data.total || 0;
      setTotalAlbaranes(total);
      setTotalPages(data.pagination?.totalPages || Math.ceil(total / ITEMS_PER_PAGE) || 1);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setAlbaranesLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchAlbaranes(1);
  }, [fetchAlbaranes]);

  // Debounced search on typing
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchAlbaranes(1, searchTerm);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAlbaranes(1, searchTerm);
  };

  const handleFiles = (files) => {
    setUploadResult(null);
    setError('');
    const parsed = files.map((file) => {
      const data = parseAlbaranFilename(file.name);
      return { file, name: file.name, parsed: data, valid: !!data };
    });
    setSelectedFiles(files);
    setParsedFiles(parsed);
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    const newParsed = [...parsedFiles];
    newFiles.splice(index, 1);
    newParsed.splice(index, 1);
    setSelectedFiles(newFiles);
    setParsedFiles(newParsed);
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setParsedFiles([]);
    setUploadResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    selectedFiles.forEach((f) => formData.append('archivos', f));

    try {
      const res = await fetch(`${API_URL}/commercial/albaranes/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al subir albaranes');
      }
      const data = await res.json();
      setUploadResult(data);
      setSelectedFiles([]);
      setParsedFiles([]);
      fetchAlbaranes(1, searchTerm);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const invalidCount = parsedFiles.filter(f => !f.valid).length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Carga de Albaranes</h1>
            <p className="text-sm text-gray-500">Sube los archivos PDF de albaranes</p>
          </div>
        </div>
      </div>

      {/* Upload-only layout */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-2xl mx-auto p-6 space-y-4">
          {/* Info Box */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex gap-3">
            <Info size={18} className="text-indigo-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-indigo-800">Formato del nombre de archivo:</p>
              <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                {'CLIENTE_REFERENCIA_ALBARAN.pdf'}
              </code>
              <p className="text-xs text-indigo-600 mt-1">
                Use <code className="bg-indigo-100 px-1 rounded">&amp;</code> en lugar de <code className="bg-indigo-100 px-1 rounded">/</code> en la referencia.
              </p>
              <p className="text-xs text-indigo-600 mt-1">
                Ej: 1234_REF&amp;2024&amp;001_ALBARAN-001.pdf
              </p>
            </div>
          </div>

          {/* Dropzone */}
          <Dropzone onFiles={handleFiles} disabled={uploading} />

          {/* Selected Files */}
          {parsedFiles.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-gray-500" />
                  <span className="font-semibold text-gray-700 text-sm">
                    {parsedFiles.length} archivo{parsedFiles.length !== 1 ? 's' : ''}
                  </span>
                  {invalidCount > 0 && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      {invalidCount} inválido{invalidCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <button onClick={clearFiles} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Limpiar
                </button>
              </div>
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-500 uppercase">
                      <th className="px-3 py-2">Archivo</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Referencia</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedFiles.map((item, idx) => (
                      <tr key={idx} className={item.valid ? 'hover:bg-gray-50' : 'bg-red-50'}>
                        <td className="px-3 py-1.5 font-mono max-w-[140px] truncate" title={item.name}>
                          {item.name}
                        </td>
                        {item.valid ? (
                          <>
                            <td className="px-3 py-1.5">{item.parsed.cliente}</td>
                            <td className="px-3 py-1.5">{item.parsed.referencia}</td>
                          </>
                        ) : (
                          <td colSpan={2} className="px-3 py-1.5 text-red-600">
                            <span className="flex items-center gap-1"><AlertCircle size={12} /> Formato inválido</span>
                          </td>
                        )}
                        <td className="px-3 py-1.5 text-center">
                          <button onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFiles.length}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 text-white font-medium text-sm rounded-lg transition-colors"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? 'Subiendo...' : 'Subir Albaranes'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                <CheckCircle size={18} />
                Resultado de la carga
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-white rounded-lg p-2 text-center border border-green-200">
                  <div className="text-xl font-bold text-green-600">{uploadResult.uploaded ?? 0}</div>
                  <div className="text-gray-500 text-xs">Subidos</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border border-yellow-200">
                  <div className="text-xl font-bold text-yellow-600">{uploadResult.skipped ?? 0}</div>
                  <div className="text-gray-500 text-xs">Omitidos</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border border-red-200">
                  <div className="text-xl font-bold text-red-600">{uploadResult.errors?.length ?? 0}</div>
                  <div className="text-gray-500 text-xs">Errores</div>
                </div>
              </div>
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-2 text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                  {uploadResult.errors.map((d, i) => (
                    <div key={i} className="px-2 py-1 rounded bg-red-100 text-red-700">
                      {d.file}: {d.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewAlbaran && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewAlbaran(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-800">
                  Albarán: {previewAlbaran.nombre_albaran || previewAlbaran.id}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <FileText size={12} />
                  Cliente: {previewAlbaran.cliente_nombre || previewAlbaran.id_cliente}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${FTP_ALBARAN_BASE}/${previewAlbaran.nombre_albaran || previewAlbaran.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
                >
                  <ExternalLink size={14} />
                  Abrir en nueva pestaña
                </a>
                <button onClick={() => setPreviewAlbaran(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* PDF iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={`${FTP_ALBARAN_BASE}/${previewAlbaran.nombre_albaran || previewAlbaran.id}`}
                className="w-full h-full border-0"
                title={`Albarán ${previewAlbaran.nombre_albaran || previewAlbaran.id}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlbaranUpload;
